import { useState } from "react";
import { ValidationFunction } from "../validations/formValidations";

export type Validations<T> = {
  [K in keyof T]?: [ValidationFunction, string][];
};

export default function useForm<T extends Record<string, string>>(
  formValues: T,
  validations: Validations<T>
) {
  const [values, setValues] = useState<T>(formValues);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});

  const handleChange = (e: React.FormEvent<HTMLFormElement>) => {
    const { name, value } = e.target as HTMLInputElement | HTMLTextAreaElement;
    setValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const validateForm = (): boolean => {
    const validationErrors: Partial<Record<keyof T, string>> = {};
    for (const key in validations) {
      const validationRules = validations[key];
      if (!validationRules) continue;
      for (const [callback, errorMessage] of validationRules) {
        const isValid = callback(values[key]);
        if (!isValid) {
          validationErrors[key] = errorMessage;
          break;
        }
      }
    }
    setErrors(validationErrors);

    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = (callback: (values: T) => void) => {
    return (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (validateForm()) {
        callback(values);
      };
    }
  };

  const resetForm = () => {
    setValues(formValues);
    setErrors({});
  }

  return {
    values,
    errors,
    handleChange,
    handleSubmit,
    resetForm,
  };
}
